import { useRef, useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useSearchQuery } from '../contexts/SearchContext';
import { useIntl } from 'react-intl';
import { usePushIndexUiState } from '../hooks/usePushIndexUiState';
import { useSearchFilters } from '../contexts/SearchContext';
import { useThrottledResize } from '../hooks/useThrottledResize';
import { useFormatMessage } from '../i18n-review/client/Msg';
import { useSearchPanelComponent } from './search-panel/search-panel-lazy';
import type { SearchPanelScope } from './search-panel/types';
import { parseUrlToIndexUiState } from '../utils/search-router/url-handling';
import { useRecentSearchesForPanel } from '../utils/search-panel/use-recent-searches-for-panel';
import { useFinlandScopeEnabled } from '../utils/search-panel/use-finland-scope-enabled';
import { ClosedSearchBar } from './navigation/nav-live-index/ClosedSearchBar';
import { isSearchPagePathname } from '../utils/routing/is-search-page-pathname';

/**
 * pushIndexUiState updates the search in place with history.pushState, which only makes sense on
 * the search results page itself. Anywhere else (company, job, salary pages...) it rewrote the
 * address bar while the page underneath stayed put, so a refresh, share or bookmark landed on
 * search results instead (seen 2026-10-07: opening the navbar search on /en/employer/elega and
 * typing moved the URL to /en, then /en/jobs?search=react).
 */
function isOnSearchResultsPage(): boolean {
  return typeof window !== 'undefined' && isSearchPagePathname(window.location.pathname);
}

export type AutocompleteHandle = {
  close: () => void;
};

type AutocompleteProps = {
  className?: string;
  placeholder?: string;
  popUp?: boolean; // If true, the SearchPanel mounts as a fixed overlay instead of an anchored dropdown (used on the mobile bottom nav)
  onOpen?: () => void; // Callback when the panel opens
  /** Squares off the input's right corners so it can sit flush against an adjacent control
   * (the SRP header's location picker button) instead of standing alone as its own pill. */
  attached?: boolean;
  /**
   * When set, Enter and facet-suggestion clicks call this with the computed jobs-listing URL
   * instead of `pushIndexUiState` (which only ever `history.pushState`s except for the
   * company-profile special case: fine for updating state in place on the SRP itself, but on
   * any other page the address bar would change while the page underneath stays put). Lets the
   * navbar live-index search launcher (TEC navbar redesign Phase 1) reuse this exact component
   * outside the SRP and do a real navigation (e.g. vike's `navigate()`) to get there. Omitted
   * everywhere else, so every existing call site (the SRP's own SearchNavbar, MobileBottomNav)
   * keeps its current in-place behaviour untouched.
   */
  onSubmitNavigate?: (url: string) => void;
  /** 'compact40'/'compact44' shrink the closed bar to a fixed 40px/44px tall (13.5px text, no
   * vertical padding) for the navbar live-index redesign: 40px for every desktop row-one
   * control, 44px for the mobile bottom nav's tappable minimum. Omitted everywhere else, so
   * every other caller keeps its current height. */
  sizeVariant?: 'default' | 'compact40' | 'compact44';
  /**
   * Coordinator review round 3, bug 5: the closed bar renders this as its own sibling (same
   * place LiveLauncher's LocationSegment sits today); once the panel opens, the SAME element
   * renders inside SearchPanel's header row instead (Main.dc.html's "Anywhere" pill), so
   * there's exactly one location control, never two.
   */
  locationSegment?: React.ReactNode;
  /** Round 10 (user feedback): a distinct node for the OPEN PANEL's own header only (the closed
   * bar keeps rendering `locationSegment` as before). SearchNavbar.tsx's own closed-bar segment
   * (tan fill, fused corner) doesn't fit the panel's raised-pill action system; this lets a caller
   * supply a different-looking control for that one spot. Falls back to `locationSegment` when
   * omitted, so a caller that only has one location control (SearchLauncher.tsx) is unaffected. */
  panelLocationSegment?: React.ReactNode;
  /** Sage search bar (2026-09-28): the filters chip, rendered inside the bar after the Where
   * chip. Omitted by hosts with no filters control of their own. */
  filtersSegment?: React.ReactNode;
  /** Live count for the forest button: the SRP's current result count, or all open jobs off the
   * SRP. null (still loading) collapses the button to a plain search icon, never a guess. */
  count?: number | null;
  /** All open jobs: the narrowing line's denominator. null hides the line. */
  countTotal?: number | null;
};

export const Autocomplete = forwardRef<AutocompleteHandle, AutocompleteProps>(
  ({ className, placeholder, popUp = false, onOpen, onSubmitNavigate, sizeVariant = 'default', locationSegment, panelLocationSegment, filtersSegment, count = null, countTotal = null }, ref) => {
  const { query: contextQuery } = useSearchQuery();
  const { pushIndexUiState } = usePushIndexUiState();
  const { filters } = useSearchFilters();
  const filtersObj = (typeof filters === 'object' && filters) ? (filters as Record<string, string[]>) : {};
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  // The exact text the closed bar shows (the `placeholder` prop if the host passed one,
  // otherwise this component's own default), reused for SearchPanel's own `placeholder` prop too
  // so the text can never visibly change between the closed and open states.
  const resolvedPlaceholder =
    placeholder ??
    formatMessage({
      id: 'instantsearch.searchbox.placeholder',
      defaultMessage: 'Search jobs, language, or city',
      description: 'Placeholder text for search input box',
    });
  const panelRecentSearches = useRecentSearchesForPanel();
  // The closed bar's own element: SearchPanel measures its rect as the expand animation's anchor.
  const barRef = useRef<HTMLButtonElement>(null);
  // The whole rounded bar (What slot + chips + count button): SearchPanel's expand animation
  // grows from this rect, so the card opens out of the full bar rather than just its text slot.
  const shellRef = useRef<HTMLDivElement>(null);
  // Coordinator review round 3, bug 2: two-phase close. Blur/close no longer unmounts SearchPanel
  // (and un-hides the bar) immediately; it flips this, SearchPanel plays its reverse animation,
  // and only its onCloseAnimationEnd callback actually unmounts + un-hides, so the two are never
  // both visible (or both invisible) at once.
  const [closingPanel, setClosingPanel] = useState(false);
  // Priority bug fix (stricter rule): focus alone must never open the panel, from any source
  // (click-to-focus, Tab, or a modal restoring focus to the bar/location control on close). Only
  // explicit intent at the bar opens it: a pointer click, or (while the bar has focus) Enter,
  // Space, ArrowDown, or typing a printable character. That last case seeds the panel's own input
  // with the typed character on the same open, via this: SearchPanel only mounts once `panelOpen`
  // flips true (a fresh mount every open), and useSearchPanel's own `pendingText` state reads
  // `scope.query` via a plain `useState` initializer (evaluated once, on that mount), so setting
  // this right before opening seeds that one keystroke without needing any prop beyond `scope`
  // itself. Reset once the panel finishes closing so a later plain click doesn't resurrect it.
  const [openSeedQuery, setOpenSeedQuery] = useState<string | null>(null);
  // Priority bug fix, third report (defensive circuit breaker): the coordinator reproduced the
  // panel flashing open with NO focus/click/key anywhere near the bar at all, ~175ms after
  // picking a value inside AllFiltersModal (autoFocus firing during React's own commitMount for a
  // freshly-mounted SearchPanel proves isOpen/isFocused genuinely both flipped true from
  // somewhere). Whatever specific state path causes that, it can only ever come from CODE OTHER
  // THAN this file's own three legitimate open triggers (the bar's onClick, its onKeyDown, and
  // focusInputOnSlash) unless it also happens to call one of those. This ref is the actual gate
  // on whether SearchPanel renders at all: only those three call sites ever set it true, and
  // every close path resets it, so a still-not-fully-traced spurious isOpen/isFocused/
  // closingPanel flip elsewhere can no longer mount the panel by itself.
  const explicitIntentRef = useRef(false);
  // The bar's three explicit-intent open paths (click, key while focused, the count button) share
  // this; `seed` is a typed printable character to carry into the panel's input.
  // Live typing adds one history entry per panel session (so Back returns to the results from
  // before the user started typing) and replaces it after that, instead of one entry per pause.
  const liveUpdatePushedRef = useRef(false);
  // The panel's code is loaded on demand (search-panel-lazy.ts). Until it arrives the closed bar
  // stays showing and keeps focus, so keys typed in the meantime land on it again; they add to the
  // seed instead of replacing it.
  const SearchPanel = useSearchPanelComponent(explicitIntentRef.current && isOpen);
  const openPanel = (seed?: string) => {
    const awaitingPanelCode = explicitIntentRef.current && isOpen && !SearchPanel;
    explicitIntentRef.current = true;
    liveUpdatePushedRef.current = false;
    if (awaitingPanelCode) {
      if (seed) setOpenSeedQuery((prev) => (prev ?? '') + seed);
    } else {
      setOpenSeedQuery(seed ?? null);
    }
    setIsOpen(true);
    setIsFocused(true);
    onOpen?.();
  };

  // Round 11 item 3 (coordinator finding, verified with headless Playwright: Escape did not
  // close the panel on the SRP at all): the panel's own SearchPanel.tsx reads Escape when a
  // location pill/facet input inside it has focus (its own keydown handling), but nothing at
  // this level ever listened for it closing the WHOLE panel the way clicking outside or
  // blurring does. Only closes an ALREADY-open panel (explicitIntentRef gates this the same way
  // it gates opening, so Escape pressed with nothing open is a no-op, never something that could
  // "open" it); focus returns to the bar button once the close animation finishes (see
  // onCloseAnimationEnd below), not here, so it lands after the reverse animation, not mid-way.
  const closedViaEscapeRef = useRef(false);
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (!explicitIntentRef.current) return;
      if (!((isOpen && isFocused) || closingPanel)) return;
      // Only Escape hands focus back to the bar once closed (below): the pill/blur/outside-click
      // close paths already send focus wherever the visitor's own action was headed (a modal, a
      // different element), and grabbing it back there would fight that.
      closedViaEscapeRef.current = true;
      setClosingPanel(true);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isFocused, closingPanel]);

  // Debounce timer for the live-results-as-you-type search update.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedSearchUpdate = useCallback((searchQuery: string) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      // Live results only exist on the search page; elsewhere the panel's own suggestions are
      // the live view and the URL must not move until the user actually submits.
      if (onSubmitNavigate || !isOnSearchResultsPage()) return;
      const urlBefore = window.location.href;
      pushIndexUiState({ query: searchQuery, refinementList: filters as any, page: 0 }, { replace: liveUpdatePushedRef.current });
      // Only a real URL change uses up the session's one entry (the panel also reports its
      // unchanged starting query when it opens).
      if (window.location.href !== urlBefore) liveUpdatePushedRef.current = true;
    }, 500);
  }, [pushIndexUiState, filters, onSubmitNavigate]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  /**
   * Search panel spec step 4 (SRP adapter). SearchPanel's `onGo`/`onApplyFilters` both hand back
   * a fully-built URL (from generateUrlFromIndexUiState, same generator SearchWrapper's own
   * getUrlForIndexUiState wraps) for "this should be the new search state" -- a free-text go, a
   * single facet applied, the parsed state's combined filters, or a widen. All four collapse to
   * the same "become this state" action, so one adapter handles all of them: round-trip the URL
   * back through parseUrlToIndexUiState (the exact inverse of generateUrlFromIndexUiState) into
   * an IndexUiState, then either hand it to the host's own navigate callback (when this
   * Autocomplete instance was mounted with onSubmitNavigate, i.e. it's not actually on the SRP)
   * or push it into SearchContext in place via pushIndexUiState.
   */
  const applyPanelUrl = useCallback(
    (url: string) => {
      if (onSubmitNavigate) {
        onSubmitNavigate(url);
        return;
      }
      // A host that didn't pass onSubmitNavigate but isn't on the search page still needs a real
      // navigation there, not an in-place pushState.
      if (!isOnSearchResultsPage()) {
        window.location.assign(url);
        return;
      }
      // This URL is the whole new search state. A live-typing update still waiting on its 500ms
      // debounce would otherwise land after it and overwrite it with the typed text and the
      // filters from before the pick.
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      pushIndexUiState(parseUrlToIndexUiState(url));
      // User feedback 2026-10-08: Enter, the go arrow, or picking/adding any suggestion means
      // "show me the results", so the panel closes onto them instead of staying open over them.
      setClosingPanel(true);
    },
    [onSubmitNavigate, pushIndexUiState],
  );

  /**
   * Live-results-as-you-type: SearchPanel's own useSearchPanel hook fires this on every
   * keystroke of ITS input (undebounced -- see onQueryTextChange in useSearchPanel.ts), and this
   * just forwards straight into debouncedSearchUpdate, so the 500ms-after-typing-stops SRP
   * update behaviour stays the same as before.
   */
  const handlePanelQueryTextChange = useCallback(
    (text: string) => {
      debouncedSearchUpdate(text);
    },
    [debouncedSearchUpdate],
  );

  // Coordinator review round 6: the panel's own counts must match the SRP's exactly, which
  // depends on the SRP's own World/Finland toggle (finlandFilter Redux slice), not a fixed
  // Finland-only assumption. See use-finland-scope-enabled.ts's own doc comment.
  const finlandScope = useFinlandScopeEnabled();
  const panelScope: SearchPanelScope = useMemo(
    () => ({ query: openSeedQuery ?? contextQuery, filters: filtersObj, locale: (intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en'), finlandScope }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openSeedQuery, contextQuery, JSON.stringify(filtersObj), intl.locale, finlandScope],
  );

  // Track if we're on mobile (md and down, which is < 768px): the fullscreen-overlay-vs-anchored-
  // dropdown container class below depends on it.
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    checkMobile();
  }, [checkMobile]);

  useThrottledResize(checkMobile);

  // Expose an imperative close: only closes an already-open panel (the explicitIntentRef gate,
  // same as every other close path), used by SearchWrapper.tsx's closeAllAutocompletes when
  // AllFiltersModal opens, so a still-open search panel and the filters modal are never both up
  // at once.
  useImperativeHandle(ref, () => ({
    close: () => {
      if (explicitIntentRef.current) {
        setClosingPanel(true);
      }
    },
  }));

  // Focus input on '/' key press: explicit intent (unlike a bare focus), so it opens the panel
  // the same way a click on the bar does.
  const focusInputOnSlash = useCallback((e: KeyboardEvent) => {
    // Only trigger if not focused in an input/textarea/select
    if (
      e.key === '/' &&
      !(document.activeElement instanceof HTMLInputElement) &&
      !(document.activeElement instanceof HTMLTextAreaElement) &&
      !(document.activeElement instanceof HTMLSelectElement)
    ) {
      e.preventDefault();
      explicitIntentRef.current = true;
      setOpenSeedQuery(null);
      setIsOpen(true);
      setIsFocused(true);
      onOpen?.();
    }
  }, [onOpen]);

  useEffect(() => {
    window.addEventListener('keydown', focusInputOnSlash);
    return () => window.removeEventListener('keydown', focusInputOnSlash);
  }, [focusInputOnSlash]);

  // Coordinator review round 6, priority bug 1: a synchronous check first, using the native
  // FocusEvent's own relatedTarget (the element about to gain focus), so a click on anything
  // still inside the card (the location pill included) never even schedules a close, rather
  // than relying solely on the 150ms-later document.activeElement read below (which is still
  // kept as a fallback for browsers/cases where relatedTarget comes back null).
  const handlePanelContainerBlur = (event: React.FocusEvent) => {
    // Gated on explicitIntentRef like every other close path. The closed bar's location segment
    // lives in this same container, so clicking it (focus moves on into AllFiltersModal) blurred
    // the container with no panel open; the ungated timer then left closingPanel stuck true, and
    // the next click on the bar mounted the panel already closing, so it flashed shut at once.
    if (!explicitIntentRef.current) return;
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && containerRef.current?.contains(nextTarget)) return;
    setTimeout(() => {
      if (explicitIntentRef.current && !containerRef.current?.contains(document.activeElement)) {
        setClosingPanel(true);
      }
    }, 150);
  };
  // Coordinator review round 6, priority bug 1: the location pill lives inside the open card
  // (trailingHeaderSlot), so a plain click on it blurs the panel's own input first (mousedown
  // moves focus away before click fires), which used to start the close before onLocationClick
  // ever ran. onMouseDown preventDefault stops the input from losing focus at all; the capture
  // handler still closes the panel (it changed the SRP's location filter, so it shouldn't stay
  // open) without racing the pill's own onClick, which fires normally afterward in the bubble
  // phase.
  // The filters chip rides along too (user feedback 2026-10-08): the closed bar, and the chip
  // with it, is hidden while the panel is open, so without this there was no way into the
  // filters modal from the open search. Same wrapper, same reasons as the location pill.
  const panelLocationContent = panelLocationSegment ?? locationSegment;
  const panelHeaderContent = panelLocationContent || filtersSegment ? (
    <>
      {filtersSegment}
      {panelLocationContent}
    </>
  ) : null;
  const locationSegmentInPanel = panelHeaderContent ? (
    <div
      className="contents"
      onMouseDown={(e) => e.preventDefault()}
      onClickCapture={() => setClosingPanel(true)}
    >
      {panelHeaderContent}
    </div>
  ) : undefined;
  // Mounted (and the bar hidden) for as long as it's either open or animating closed; only the
  // reverse-animation's own completion callback actually unmounts it. explicitIntentRef is the
  // actual gate (see its own declaration comment): isOpen/isFocused/closingPanel alone are not
  // trusted to only ever be set by real user intent.
  const panelOpen = explicitIntentRef.current && ((isOpen && isFocused) || closingPanel) && SearchPanel !== null;
  // Priority bug fix, stricter rule (second report): focus alone must NEVER open the panel,
  // from ANY source, including a keyboard Tab landing on the bar, and including a modal (e.g.
  // AllFiltersModal) restoring focus to the bar or the location control when IT closes (a
  // `:focus-visible` check still let that case through, since a programmatic focus restore
  // after Escape/a keyboard close reads as focus-visible too). There is deliberately no
  // onFocus handler on this container at all: opening only ever happens from the bar button's
  // own onClick (a pointer click) or onKeyDown (Enter/Space/ArrowDown, or typing a printable
  // character while the bar has focus, which also seeds that character into the panel's own
  // input via openSeedQuery), or from the "/" shortcut (focusInputOnSlash above).
  return (
    <div
      ref={containerRef}
      className={'relative w-full z-(--jc-z-dropdown) ' + (className || '')}
      data-component="autocomplete"
      onBlur={handlePanelContainerBlur}
    >
      {/* Coordinator review round 2/3: the bar (and its chips) stay mounted (never display:none)
       * so the bar's rect is always measurable for the expand animation's anchor, and so there is
       * never a visible "double bar" moment; `visibility: hidden` (not display:none) preserves
       * layout while the panel is open. The location chip moves into SearchPanel's own header
       * (trailingHeaderSlot) once open, per bug 5.
       *
       * Sage search bar (2026-09-28): one rounded field holding the What slot (this button, the
       * only part that opens the panel), the Where chip, the filters chip, the forest count
       * button and the narrowing line. Pointer or focus anywhere on it stops the teaser. */}
      <ClosedSearchBar
        shellRef={shellRef}
        barRef={barRef}
        hidden={panelOpen}
        placeholder={resolvedPlaceholder}
        query={contextQuery}
        onOpen={openPanel}
        locationSegment={locationSegment}
        filtersSegment={filtersSegment}
        count={count}
        countTotal={countTotal}
        sizeVariant={sizeVariant === 'compact44' ? 'compact44' : 'compact40'}
      />
      {panelOpen && SearchPanel && (
        <div className={isMobile ? 'fixed left-0 right-0 top-0 z-(--jc-z-dropdown)' : 'absolute left-0 right-0 top-0 z-(--jc-z-dropdown)'}>
          <SearchPanel
            variant="dropdown"
            scope={panelScope}
            onGo={applyPanelUrl}
            onApplyFilters={applyPanelUrl}
            onQueryTextChange={handlePanelQueryTextChange}
            recentSearches={panelRecentSearches}
            anchorRef={shellRef}
            autoFocus
            trailingHeaderSlot={locationSegmentInPanel}
            placeholder={resolvedPlaceholder}
            closing={closingPanel}
            onCloseAnimationEnd={() => {
              setIsOpen(false);
              setIsFocused(false);
              setClosingPanel(false);
              setOpenSeedQuery(null);
              explicitIntentRef.current = false;
              // Round 11 item 3: Escape hands focus back to the bar once the reverse animation
              // has actually finished, not mid-close; a plain .focus() call, which per the
              // stricter focus rule above never reopens anything on its own.
              if (closedViaEscapeRef.current) {
                closedViaEscapeRef.current = false;
                barRef.current?.focus();
              }
            }}
          />
        </div>
      )}
    </div>
  );
});

Autocomplete.displayName = 'Autocomplete';
